import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import RouteMap from '../../components/RouteMap/RouteMap';
import { searchActivities, getMyProfile, avatarUrl } from '../../api/client';
import AvatarImage from '../../components/Avatar/AvatarImage';
import { canManageActivities } from '../../auth/roles';
import {
  resolveActivityType,
  getActivityStats,
  classifyRouteType,
} from '../../activities/activityTypes';
import ActivityTypeIcon from '../../activities/ActivityTypeIcon';
import AddActivityModal from '../../components/AddActivityModal/AddActivityModal';
import './HomePage.css';

/**
 * Private landing page (route "/"), reached right after login.
 *
 * It renders the FLIK route explorer: a left rail with search, filters and a
 * route list, and an OpenStreetMap surface centred on the user's location. Both
 * the list and the featured card are driven by the same activities the map
 * fetches for the current viewport (GET /activities), so everything stays in
 * sync as the user pans and zooms. No activity is selected by default.
 */

// A fixed "Todo" chip; the rest of the filter chips are derived from the
// activity types actually present in the current viewport.
const ALL_FILTER = { id: 'todo', label: 'Todo' };

const NAV_ITEMS = [
  { id: 'mapa', label: 'Mapa', icon: MapIcon },
  { id: 'favoritos', label: 'Favoritos', icon: StarIcon },
  { id: 'perfil', label: 'Perfil', icon: UserIcon },
];

function HomePage() {
  const premium = process.env.REACT_APP_PREMIUM_ENABLED === 'true';
  const navigate = useNavigate();
  const mapApiRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('todo');
  const [selectedId, setSelectedId] = useState(null);
  const [favourites, setFavourites] = useState({});
  const [activeNav, setActiveNav] = useState('mapa');

  // Activities returned for the current map viewport, plus the corners of that
  // viewport (the polygon the backend filters by). Bumping `reqId` lets a stale
  // response — one for a viewport the user has already left — be discarded.
  const [activities, setActivities] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const reqIdRef = useRef(0);

  // Whether the signed-in user may add activities (ADMIN / EDITOR) and whether
  // the "add activity" modal is open.
  const [canManage, setCanManage] = useState(false);
  const [adding, setAdding] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [avatarCrop, setAvatarCrop] = useState(null);

  // The map reports its viewport (debounced) whenever it settles.
  const handleBoundsChange = useCallback((corners) => setBounds(corners), []);

  // Resolve the role once, to decide whether to surface the "add" button.
  useEffect(() => {
    let alive = true;
    getMyProfile()
      .then((me) => {
        if (!alive) return;
        setCanManage(canManageActivities(me));
        setAvatar(avatarUrl(me?.profileImageId ?? me?.avatarId));
        const cx = me?.avatarCropX ?? me?.avatar_crop_x;
        const cw = me?.avatarCropWidth ?? me?.avatar_crop_width;
        setAvatarCrop(
          cx != null && cw != null
            ? {
                x: cx,
                y: me?.avatarCropY ?? me?.avatar_crop_y,
                width: cw,
                height: me?.avatarCropHeight ?? me?.avatar_crop_height,
              }
            : null
        );
      })
      .catch(() => {
        if (alive) setCanManage(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Re-query the activities whenever the viewport changes or a new one is added.
  useEffect(() => {
    if (!bounds) return undefined;
    const reqId = ++reqIdRef.current;
    let alive = true;
    searchActivities({ bounds })
      .then((list) => {
        if (!alive || reqId !== reqIdRef.current) return;
        setActivities(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        // Leave the previous list in place on a network/auth failure.
      });
    return () => {
      alive = false;
    };
  }, [bounds, refreshKey]);

  const handleNav = (id) => {
    if (id === 'perfil') {
      navigate('/perfil');
      return;
    }
    setActiveNav(id);
  };

  // Filter chips: "Todo" plus one per distinct activity type in view.
  const filters = useMemo(() => {
    const types = [...new Set(activities.map((a) => a.activityType).filter(Boolean))];
    return [
      ALL_FILTER,
      ...types.map((t) => ({ id: t, label: resolveActivityType(t).label, type: t })),
    ];
  }, [activities]);

  const filteredActivities = useMemo(() => {
    if (activeFilter === 'todo') return activities;
    return activities.filter((a) => a.activityType === activeFilter);
  }, [activities, activeFilter]);

  // Only a user-picked activity is "open" — nothing is selected by default.
  const selected = filteredActivities.find((a) => a.id === selectedId) || null;

  // Map legend: the distinct activity types currently on the map, each with its
  // colour and a count. Built from what's actually in view (post-filter) so the
  // legend and the pins always agree. Types with no defined icon collapse into
  // the FLIK fallback entry.
  const legend = useMemo(() => {
    const byKey = new Map();
    for (const a of filteredActivities) {
      const lat = Number(a?.values?.latitude);
      const lng = Number(a?.values?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const t = resolveActivityType(a.activityType);
      const entry = byKey.get(t.key);
      if (entry) entry.count += 1;
      else byKey.set(t.key, { key: t.key, label: t.label, color: t.color, count: 1 });
    }
    return [...byKey.values()];
  }, [filteredActivities]);
  const [legendOpen, setLegendOpen] = useState(false);

  const selectActivity = (activity) => {
    setSelectedId(activity.id);
    if (mapApiRef.current && mapApiRef.current.focusActivity) {
      mapApiRef.current.focusActivity(activity);
    }
  };

  const closeCard = () => setSelectedId(null);

  const toggleFavourite = (id) =>
    setFavourites((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="Home">
      {/* ---- Left rail ---- */}
      <aside className="Home__sidebar">
        <header className="Home__brand">
          <FlikLogo height={20} />
          <button
            type="button"
            className="Home__avatar"
            onClick={() => navigate('/perfil')}
            title="Tu perfil"
            aria-label="Tu perfil"
          >
            {avatar ? (
              <AvatarImage className="Home__avatarImg" src={avatar} crop={avatarCrop} />
            ) : (
              <UserIcon />
            )}
          </button>
        </header>

        <div className="Home__search">
          <PinIcon />
          <input
            type="search"
            placeholder="Buscar ruta, clima o barranco"
            aria-label="Buscar ruta, clima o barranco"
          />
        </div>

        <div className="Home__filters" role="group" aria-label="Filtros">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`Home__chip${activeFilter === f.id ? ' is-active' : ''}`}
              aria-pressed={activeFilter === f.id}
              onClick={() => setActiveFilter(f.id)}
            >
              <span className="Home__chipIcon" aria-hidden="true">
                <ActivityTypeIcon activityType={f.type} size={15} />
              </span>
              {f.label}
            </button>
          ))}
        </div>

        <p className="Home__count">
          {filteredActivities.length}{' '}
          {filteredActivities.length === 1 ? 'actividad' : 'actividades'} en esta zona
        </p>

        <ul className="Home__routes">
          {filteredActivities.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                className={`RouteItem${selectedId === a.id ? ' is-active' : ''}`}
                onClick={() => selectActivity(a)}
              >
                <div className="RouteItem__head">
                  <span className="RouteItem__type" aria-hidden="true">
                    <ActivityTypeIcon activityType={a.activityType} size={16} />
                  </span>
                  <span className="RouteItem__name">{a.name}</span>
                  <span
                    className={`RouteItem__star${favourites[a.id] ? ' is-on' : ''}`}
                    aria-hidden="true"
                  >
                    <StarIcon filled={favourites[a.id]} />
                  </span>
                </div>
                <div className="RouteItem__meta">
                  {getActivityStats(a).map((stat, i) => (
                    <React.Fragment key={stat.unit}>
                      {i > 0 && <span className="RouteItem__metaSep"> · </span>}
                      <StatValue stat={stat} />
                      {stat.kind === 'bool' || stat.kind === 'routeType' ? null : (
                        <span className="RouteItem__metaUnit"> {stat.unit}</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                {selectedId === a.id && a.activityType && (
                  <div className="RouteItem__tags">
                    <span className="RouteItem__tag">{a.activityType.toUpperCase()}</span>
                  </div>
                )}
              </button>
            </li>
          ))}
          {filteredActivities.length === 0 && (
            <li className="Home__empty">No hay actividades en esta zona.</li>
          )}
        </ul>

        {canManage && (
          <button
            type="button"
            className="Home__addActivity"
            onClick={() => setAdding(true)}
          >
            <span className="Home__addActivityIcon" aria-hidden="true">
              +
            </span>
            Añadir actividad
          </button>
        )}
        {premium && (<div className="Home__premium">
          <span className="Home__premiumTitle">Hazte Premium</span>
          <span className="Home__premiumText">
            Mapas offline, GPX ilimitados y retos exclusivos.
          </span>
        </div>)
          
        }
        
      </aside>

      {/* ---- Map ---- */}
      <section className="Home__map">
        <RouteMap
          ref={mapApiRef}
          activities={filteredActivities}
          selectedId={selectedId}
          onBoundsChange={handleBoundsChange}
          onActivitySelect={selectActivity}
        />

        {/* Map legend — colour ↔ activity type. Collapsible on phones so it
            never eats the map; always expanded on desktop via CSS. */}
        {legend.length > 0 && (
          <div className={`MapLegend${legendOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="MapLegend__toggle"
              aria-expanded={legendOpen}
              onClick={() => setLegendOpen((v) => !v)}
            >
              <span className="MapLegend__eyebrow">Leyenda</span>
              <span className="MapLegend__chevron" aria-hidden="true" />
            </button>
            <ul className="MapLegend__list">
              {legend.map((item) => (
                <li key={item.key} className="MapLegend__item">
                  <span
                    className="MapLegend__swatch"
                    style={{ background: item.color }}
                    aria-hidden="true"
                  />
                  <span className="MapLegend__label">{item.label}</span>
                  <span className="MapLegend__count">{item.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mobile-only floating controls: search + filters over the map.
            Hidden on desktop, where the sidebar owns them. */}
        <div className="Home__mapTop">
          <span className="Home__mapEyebrow">Mapa</span>
          <div className="Home__mapSearchRow">
            <div className="Home__search Home__search--float">
              <PinIcon />
              <input
                type="search"
                placeholder="Buscar ruta o cima"
                aria-label="Buscar ruta o cima"
              />
            </div>
            <button
              type="button"
              className="Home__mapType"
              onClick={() => mapApiRef.current && mapApiRef.current.cycleLayer()}
              aria-label="Cambiar tipo de mapa"
            >
              <LayersIcon />
            </button>
          </div>
          <div
            className="Home__filters Home__filters--float"
            role="group"
            aria-label="Filtros"
          >
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`Home__chip Home__chip--float${
                  activeFilter === f.id ? ' is-active' : ''
                }`}
                aria-pressed={activeFilter === f.id}
                onClick={() => setActiveFilter(f.id)}
              >
                <span className="Home__chipIcon" aria-hidden="true">
                  <ActivityTypeIcon activityType={f.type} size={15} />
                </span>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {selected && (
          <article className="RouteCard">
            <button
              type="button"
              className="RouteCard__close"
              onClick={closeCard}
              aria-label="Cerrar"
            >
              ×
            </button>
            <div className="RouteCard__thumb" aria-hidden="true">
              <span className="RouteCard__badge">
                <ActivityTypeIcon activityType={selected.activityType} size={18} />
                {resolveActivityType(selected.activityType).label}
              </span>
            </div>
            <div className="RouteCard__body">
              <div className="RouteCard__titleRow">
                <div>
                  <h2 className="RouteCard__title">{selected.name}</h2>
                  {selected.activityType && (
                    <p className="RouteCard__place">
                      <span className="RouteCard__placeIcon" aria-hidden="true">
                        <ActivityTypeIcon activityType={selected.activityType} size={15} />
                      </span>
                      <span className="RouteCard__placeFull">
                        {resolveActivityType(selected.activityType).label.toUpperCase()}
                      </span>
                    </p>
                  )}
                </div>
                <div className="RouteCard__actions">
                  <button
                    type="button"
                    className={`RouteCard__icon${favourites[selected.id] ? ' is-on' : ''}`}
                    onClick={() => toggleFavourite(selected.id)}
                    aria-label="Guardar en favoritos"
                  >
                    <StarIcon filled={favourites[selected.id]} />
                  </button>
                  <button type="button" className="RouteCard__icon" aria-label="Compartir">
                    <ShareIcon />
                  </button>
                </div>
              </div>

              <dl className="RouteCard__stats">
                {getActivityStats(selected).map((stat) => (
                  <div key={stat.unit}>
                    <dt>
                      <StatValue stat={stat} />
                    </dt>
                    <dd>{stat.unit}</dd>
                  </div>
                ))}
              </dl>

              <div className="RouteCard__cta">
                <button
                  type="button"
                  className="RouteCard__primary"
                  onClick={() =>
                    navigate(`/actividad/${selected.id}`, { state: { activity: selected } })
                  }
                >
                  Ver ruta
                  <span className="RouteCard__arrow" aria-hidden="true">
                    →
                  </span>
                </button>
                {selected.values?.gpx && (
                  <button type="button" className="RouteCard__secondary">
                    Descargar GPX
                  </button>
                )}
              </div>
            </div>
          </article>
        )}
      </section>

      {/* ---- Mobile bottom nav ---- */}
      <nav className="Home__nav" aria-label="Navegación principal">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`Home__navItem${activeNav === id ? ' is-active' : ''}`}
            aria-current={activeNav === id ? 'page' : undefined}
            onClick={() => handleNav(id)}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {canManage && !selected && (
        <button
          type="button"
          className="Home__fab"
          onClick={() => setAdding(true)}
          aria-label="Añadir actividad"
        >
          +
        </button>
      )}

      {adding && (
        <AddActivityModal
          onClose={() => setAdding(false)}
          onCreated={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

/**
 * Renders one resolved activity stat's value. Most stats are a plain scalar;
 * booleans (e.g. "Equipada") show a check / X, and the ciclismo route type
 * shows an MTB or road-bike icon plus its label.
 */
function StatValue({ stat }) {
  if (!stat.present) return <span className="StatVal StatVal--empty">—</span>;

  if (stat.kind === 'bool') {
    return stat.value ? (
      <span className="StatVal StatVal--yes" title="Equipada" aria-label="Sí">
        <CheckIcon />
      </span>
    ) : (
      <span className="StatVal StatVal--no" title="No equipada" aria-label="No">
        <CrossIcon />
      </span>
    );
  }

  if (stat.kind === 'routeType') {
    const kind = classifyRouteType(stat.value);
    return (
      <span className="StatVal StatVal--route">
        {kind === 'mtb' ? <MtbIcon /> : <RoadBikeIcon />}
        <span className="StatVal__routeLabel">{kind === 'mtb' ? 'MTB' : 'Carretera'}</span>
      </span>
    );
  }

  return <span className="StatVal">{stat.value}</span>;
}

/* ---- Inline icons (no asset dependencies) ---- */

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M4 12.5 9.5 18 20 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M6 6 18 18M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* Mountain bike: fat knobby wheels + flat bar. */
function MtbIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="5.5" cy="16" r="4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="18.5" cy="16" r="4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5.5 16 10 9h6l2.5 7M10 9l-2-2H6M16 9l1.5 7M10 9h5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Road bike: thin wheels + dropped handlebar. */
function RoadBikeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="5.5" cy="16" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="18.5" cy="16" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M5.5 16 10.5 8.5h5L18.5 16M10.5 8.5 9 7.5M9 7.5h2.5M9 7.5c-.6 0-1 .5-1 1.2M15.5 8.5 14 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path
        d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <circle cx="12" cy="10" r="2.4" fill="currentColor" />
    </svg>
  );
}

function StarIcon({ filled = false }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7L12 3Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M12 3v13M8 7l4-4 4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="m9 4-6 2v14l6-2 6 2 6-2V4l-6 2-6-2Zm0 0v14m6-12v14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LayersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
      <path
        d="M12 3 3 8l9 5 9-5-9-5Zm-9 8 9 5 9-5m-18 5 9 5 9-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default HomePage;
